import { z } from 'zod'

/**
 * A digital board: cells on a canvas of a fixed ratio, each cell rotating a
 * playlist of widgets.
 *
 * A cell is a rectangle of whole units on the ratio's snapping grid
 * (`boardGrid`). The schema holds any such list; `cellProblem` is what refuses
 * one that cannot stand — off the canvas, overlapping, or smaller than a
 * widget it holds needs — so every write path refuses the same shapes.
 */

export const BOARD_RATIOS = ['16:9', '9:16', '10:7', '7:10'] as const
export const boardRatio = z.enum(BOARD_RATIOS)
export type BoardRatio = z.infer<typeof boardRatio>

export const boardCell = z.strictObject({
  id: z.string().min(1).max(80),
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  w: z.number().int().min(1),
  h: z.number().int().min(1),
})

export const boardLayout = z.strictObject({
  ratio: boardRatio,
  cells: z.array(boardCell),
})

/**
 * When an entry may show. A window of dates is the shul's own civil days,
 * inclusive; an occasion window is live while that occasion leads and is under
 * way, so it needs no new dates each year.
 */
export const boardWindow = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('dates'),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  }),
  z.strictObject({ kind: z.literal('occasion'), occasionKey: z.string() }),
])

/**
 * Where a zmanim entry's day ends and tomorrow's zmanim begin: midnight, or a
 * zman named by its catalog key. Each tzeis is its own shita, so the shul names
 * the one it keeps rather than the board choosing.
 */
export const ZMANIM_TURNS = [
  'midnight',
  'sunset',
  'tzeis3stars',
  'tzeis72deg',
  'tzeis72fix',
] as const
export type ZmanimTurn = (typeof ZMANIM_TURNS)[number]

export const boardWidget = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('clock'),
    face: z.enum(['digital', 'analog']).default('digital'),
  }),
  z.strictObject({
    kind: z.literal('zmanim'),
    /** Absent is midnight, the civil date's own turn. */
    turns: z.enum(ZMANIM_TURNS).optional(),
  }),
  z.strictObject({ kind: z.literal('schedule') }),
  z.strictObject({
    kind: z.literal('announcement'),
    title: z.string().max(120),
    body: z.string().max(600),
  }),
  z.strictObject({ kind: z.literal('page'), key: z.string().min(1).max(16) }),
  z.strictObject({
    kind: z.literal('give'),
    target: z.discriminatedUnion('kind', [
      z.strictObject({ kind: z.literal('menu') }),
      z.strictObject({
        kind: z.literal('campaign'),
        campaignShortId: z.string(),
      }),
    ]),
  }),
  z.strictObject({ kind: z.literal('pay') }),
])

export const boardEntry = z.strictObject({
  widget: boardWidget,
  /** How long it holds the zone before the rotation moves on. */
  seconds: z.number().int().min(5).max(600),
  window: boardWindow.nullable(),
  shabbosSafe: z.boolean(),
  /** While its window is live it is the only entry its zone shows. */
  takeover: z.boolean(),
})

export const board = z
  .strictObject({
    layout: boardLayout,
    /** Each cell's playlist, keyed by the cell's id. */
    zones: z.record(z.string(), z.array(boardEntry)),
    /**
     * What the board does inside Shabbos and Yom Tov: keep rotating its
     * Shabbos-safe entries, or hold each zone still on its first one. Rulings
     * differ, so it is each shul's to set; the clock runs either way.
     */
    onShabbos: z.enum(['rotate', 'hold']).default('rotate'),
    /** Boards that take this one's place while their windows hold, in priority order. */
    standIns: z
      .array(
        z.strictObject({
          boardId: z.string().uuid(),
          window: boardWindow,
        }),
      )
      .default([]),
  })
  .superRefine((value, ctx) => {
    const problem = cellProblem(value)
    if (problem) ctx.addIssue({ code: 'custom', message: problem })
  })

export type BoardLayout = z.infer<typeof boardLayout>
export type BoardCell = z.infer<typeof boardCell>
export type BoardWindow = z.infer<typeof boardWindow>
export type BoardWidget = z.infer<typeof boardWidget>
export type BoardEntry = z.infer<typeof boardEntry>
export type ParsedBoard = z.output<typeof board>
export type BoardStandIn = ParsedBoard['standIns'][number]
/** A definition may omit what the schema fills by default. */
export type Board = Omit<ParsedBoard, 'standIns' | 'onShabbos'> & {
  standIns?: BoardStandIn[]
  onShabbos?: ParsedBoard['onShabbos']
}

/**
 * Each widget's smallest cell, and the size a new cell for it starts at, in
 * grid units on the 24-unit long side. One table so they are tuned in one
 * place. `full` is the canvas's short side.
 */
export const WIDGET_SIZES = {
  'clock:digital': { min: [3, 2], default: [6, 3] },
  'clock:analog': { min: [3, 3], default: [5, 5] },
  zmanim: { min: [6, 6], default: [8, 'full'] },
  schedule: { min: [6, 6], default: [8, 'full'] },
  announcement: { min: [6, 4], default: [12, 7] },
  page: { min: [8, 6], default: [16, 10] },
  give: { min: [4, 3], default: [6, 4] },
  pay: { min: [4, 3], default: [6, 4] },
} as const

/** The canvases on which each widget kind can be offered and saved. */
export const WIDGET_CANVASES: Record<
  BoardWidget['kind'],
  readonly BoardRatio[]
> = {
  clock: BOARD_RATIOS,
  zmanim: BOARD_RATIOS,
  schedule: BOARD_RATIOS,
  announcement: BOARD_RATIOS,
  page: BOARD_RATIOS,
  give: ['10:7', '7:10'],
  pay: ['10:7', '7:10'],
}

/** Whether a widget kind is offered on a board ratio. */
export function widgetOffered(
  kind: BoardWidget['kind'],
  ratio: BoardRatio,
): boolean {
  return WIDGET_CANVASES[kind].includes(ratio)
}

/** Whether each widget kind may remain live during Shabbos or Yom Tov. */
export const WIDGET_MAY_SHOW_DURING_REST = {
  clock: true,
  zmanim: true,
  schedule: true,
  announcement: true,
  page: true,
  give: false,
  pay: false,
} as const satisfies Record<BoardWidget['kind'], boolean>

/** The starting shape for a widget kind wherever the editor creates one. */
export function startingBoardWidget(
  kind: BoardWidget['kind'],
  clockFace: 'digital' | 'analog' = 'digital',
): BoardWidget {
  switch (kind) {
    case 'clock':
      return { kind, face: clockFace }
    case 'zmanim':
    case 'schedule':
      return { kind }
    case 'announcement':
      return { kind, title: '', body: '' }
    case 'page':
      return { kind, key: '' }
    case 'give':
      return { kind, target: { kind: 'menu' } }
    case 'pay':
      return { kind }
  }
}

/**
 * The snapping grid for a ratio: 24 units on the long side, the short side
 * the same unit rounded half up. The canvas still draws at the board's true
 * ratio, so a unit need not be square; rounding a half up gives the short
 * side the finer resolution.
 */
export function boardGrid(ratio: BoardRatio): {
  width: number
  height: number
} {
  const [a, b] = ratioParts(ratio)
  return a >= b
    ? { width: 24, height: Math.floor((24 * b) / a + 0.5) }
    : { width: Math.floor((24 * a) / b + 0.5), height: 24 }
}

function ratioParts(ratio: BoardRatio): [number, number] {
  return ratio.split(':').map(Number) as [number, number]
}

/** A ratio as one number, across over down: what a canvas is drawn at. */
export function ratioValue(ratio: BoardRatio): number {
  const [across, down] = ratioParts(ratio)
  return across / down
}

const widgetSize = (widget: BoardWidget) => {
  if (widget.kind === 'clock') return WIDGET_SIZES[`clock:${widget.face}`]
  return WIDGET_SIZES[widget.kind]
}

/**
 * A widget's smallest cell and the cell it starts in on a ratio's grid, with
 * `full` resolved and the start held inside the canvas.
 */
export function widgetFit(
  widget: BoardWidget,
  ratio: BoardRatio,
): { min: [number, number]; start: [number, number] } {
  const { width, height } = boardGrid(ratio)
  const size = widgetSize(widget)
  const short = Math.min(width, height)
  const [w, h] = size.default.map((n) => (n === 'full' ? short : n))
  return {
    min: [size.min[0], size.min[1]],
    start: [Math.min(w, width), Math.min(h, height)],
  }
}

/**
 * The smallest a cell may be: the largest minimum among the widgets it
 * rotates, so every one of them fits, or one unit for a cell holding none.
 */
export function cellMinimum(entries: BoardEntry[]): [number, number] {
  let w = 1
  let h = 1
  for (const entry of entries) {
    const [minW, minH] = widgetSize(entry.widget).min
    w = Math.max(w, minW)
    h = Math.max(h, minH)
  }
  return [w, h]
}

/**
 * A cell as a person reads it, on the editor's map and a screen's codes alike:
 * its place in the board's list. The id is the playlist's key and is never
 * shown, so a refusal naming it would name nothing anybody can find.
 */
export function cellCode(layout: BoardLayout, cell: BoardCell): string {
  return `Cell ${layout.cells.indexOf(cell) + 1}`
}

/**
 * Why a board's cells cannot stand: none at all, a repeated id, a cell with no
 * area, one that leaves the canvas, one smaller than its widget's minimum, or
 * two that overlap. Names the cell by its code so the editor can point at it.
 */
export function cellProblem(boardValue: {
  layout: BoardLayout
  zones: Record<string, BoardEntry[]>
}): string | null {
  const { layout } = boardValue
  const { width, height } = boardGrid(layout.ratio)
  const cells = layout.cells
  const code = (cell: BoardCell) => cellCode(layout, cell)
  // A board with no cell is a blank screen in the building, never a layout.
  if (cells.length === 0) return 'A board needs at least one cell.'
  const ids = new Set<string>()
  for (const cell of cells) {
    if (ids.has(cell.id)) return `${code(cell)} repeats another cell's id.`
    ids.add(cell.id)
    if (cell.w < 1 || cell.h < 1) return `${code(cell)} would have no area.`
    if (cell.x + cell.w > width || cell.y + cell.h > height)
      return `${code(cell)} leaves the canvas.`
    for (const entry of boardValue.zones[cell.id] ?? []) {
      if (!widgetOffered(entry.widget.kind, layout.ratio))
        return `${code(cell)} uses a kiosk action, which only a tablet board offers.`
      const [minW, minH] = widgetSize(entry.widget).min
      if (cell.w < minW || cell.h < minH)
        return `${code(cell)} would be smaller than its ${entry.widget.kind} needs.`
    }
  }
  for (let i = 0; i < cells.length; i++)
    for (let j = i + 1; j < cells.length; j++)
      if (cellsOverlap(cells[i], cells[j]))
        return `${code(cells[j])} overlaps ${code(cells[i])}.`
  return null
}

type Rect = Pick<BoardCell, 'x' | 'y' | 'w' | 'h'>

/** Whether two rectangles share any area; touching along an edge is not overlap. */
export function cellsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h
  )
}

/**
 * A board redrawn at another ratio. Each cell's four edges scale and round
 * half up, then width and height come from those edges, so cells that shared
 * a boundary still share it. Scaling x/y/w/h independently would round a
 * shared edge twice and leave a gap or an overlap. Refused, naming the cell,
 * if one ends up under its minimum or overlapping another.
 */
export function scaleBoardRatio(
  boardValue: Board,
  ratio: BoardRatio,
): { board?: Board; error?: string } {
  const from = boardGrid(boardValue.layout.ratio)
  const to = boardGrid(ratio)
  const edge = (n: number, old: number, next: number) =>
    Math.floor((n * next) / old + 0.5)
  const cells = boardValue.layout.cells.map((cell) => {
    const x = edge(cell.x, from.width, to.width)
    const y = edge(cell.y, from.height, to.height)
    const right = edge(cell.x + cell.w, from.width, to.width)
    const bottom = edge(cell.y + cell.h, from.height, to.height)
    return { ...cell, x, y, w: right - x, h: bottom - y }
  })
  const next = { ...boardValue, layout: { ratio, cells } }
  const error = cellProblem(next)
  return error ? { error } : { board: next }
}

/** What a playlist is decided against: the moment, as the shul's day sees it. */
export type BoardMoment = {
  /** The shul's own civil date. */
  date: string
  /** The occasion under way at the moment, if one is. */
  occasionKey: string | null
  /** Inside Shabbos or Yom Tov. */
  rest: boolean
}

export function windowHolds(
  window: BoardWindow | null,
  at: BoardMoment,
): boolean {
  if (!window) return true
  if (window.kind === 'dates')
    return window.from <= at.date && at.date <= window.to
  return window.occasionKey === at.occasionKey
}

/** The first board scheduled to take this one's place at the moment. */
export function standingIn(
  standIns: BoardStandIn[],
  at: BoardMoment,
): string | null {
  return (
    standIns.find((standIn) => windowHolds(standIn.window, at))?.boardId ?? null
  )
}

/**
 * The entries a zone rotates through at a moment.
 *
 * An entry outside its window, or not Shabbos-safe inside Shabbos or Yom Tov,
 * is not there. A live takeover stands alone — the rest of the playlist is
 * untouched, so when its window closes the rotation simply carries on.
 */
export function liveEntries(
  entries: BoardEntry[],
  at: BoardMoment,
): BoardEntry[] {
  const live = entries.filter(
    (e) =>
      windowHolds(e.window, at) &&
      (!at.rest ||
        (e.shabbosSafe && WIDGET_MAY_SHOW_DURING_REST[e.widget.kind])),
  )
  const takeovers = live.filter((e) => e.takeover && e.window)
  return takeovers.length ? takeovers : live
}

/**
 * How far ahead a screen holds the server's answers, so an outage across a
 * three-day Yom Tov still shows right times. Past it a screen shows no time
 * rather than an earlier day's.
 */
export const BOARD_AHEAD_HOURS = 72

/**
 * Whether the board's zones stand still now: a board set to hold, inside the
 * rest the server reported. Each zone then shows its first live entry, which
 * inside rest is its first Shabbos-safe one.
 */
export function holdsStill(
  board: Pick<Board, 'onShabbos'>,
  at: BoardMoment,
): boolean {
  return board.onShabbos === 'hold' && at.rest
}

/**
 * Which day's zmanim an entry shows at `now`: tomorrow's once today's turn has
 * passed. Midnight is the civil date's own turn, so it never asks for tomorrow;
 * a turn the server reported no instant for leaves today standing.
 */
export function zmanimDay(
  turn: ZmanimTurn | undefined,
  turns: Record<string, string>,
  now: number,
): 'today' | 'tomorrow' {
  if (!turn || turn === 'midnight') return 'today'
  const at = turns[turn]
  return at && Date.parse(at) <= now ? 'tomorrow' : 'today'
}

/**
 * A board as a link fragment: base64url of its JSON. The link carries the
 * whole board, so a screen opens it with no session and nothing stored.
 */
export function encodeBoard(value: Board): string {
  const bytes = new TextEncoder().encode(JSON.stringify(value))
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** The board a fragment carries, or null where it carries none we can read. */
export function decodeBoard(fragment: string): Board | null {
  try {
    const b64 = fragment.replace(/-/g, '+').replace(/_/g, '/')
    const bin = atob(b64)
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0))
    const parsed = board.safeParse(JSON.parse(new TextDecoder().decode(bytes)))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}
